import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { FileDownloadOutlined } from "@mui/icons-material";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { SwayyPageHeader } from "../../components/SwayyPageShell";
import SwayyStatusTabs, {
  SWAYY_SHIPMENT_STATUS_TAB_ICONS,
} from "../../components/SwayyStatusTabs";
import SwayySearchFiltersPanel, {
  SwayyFilterDateField,
  SwayyFilterDropdownField,
  SwayyFilterSearchField,
  SwayyFilterSearchInput,
  SWAYY_FILTER_DROPDOWN_DEFAULTS,
} from "../../components/filters/SwayySearchFiltersPanel";
import SwayyShipmentsTable, {
  SwayyShipmentAction,
} from "../../components/shipments/SwayyShipmentsTable";
import SwayyShipmentDetailDrawer from "../../components/shipments/SwayyShipmentDetailDrawer";
import {
  SWAYY_SHIPMENT_DEFAULT_STATUS_TAB,
  SWAYY_SHIPMENT_STATUS_TABS,
} from "../../../../../models/SwayyShipment/SwayyShipmentFilters";
import {
  SwayyCourierPartner,
  SwayyShipment,
} from "../../../../../models/SwayyShipment/SwayyShipment";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { swayyShipmentStore } from "../../../stores/SwayyShipmentStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";

const OVERDUE_OPTIONS: FilterValue[] = [
  { id: "true", value: "Overdue only" },
  { id: "false", value: "Not overdue" },
];

const SwayyShipments = () => {
  const store = swayyShipmentStore;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError } = useNotification();
  const [urlReady, setUrlReady] = useState(false);
  const [csvMenuEl, setCsvMenuEl] = useState<HTMLElement | null>(null);
  const csvButtonRef = useRef<HTMLDivElement | null>(null);
  const awbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLoadShipments = async () => {
    try {
      await store.getShipments();
    } catch {
      showError("Error", "Failed to load shipments.");
    }
  };

  useEffect(() => {
    if (
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_SHIPMENTS,
        SwayyEmployeeRoles.EDIT_SHIPMENTS,
      ])
    ) {
      return;
    }

    store.initializeParamsFromSearch(searchParams);
    store.setDraftCarrierId([]);
    store.setDraftWarehouseId([]);
    store.setParams({ carrier_id: "", warehouse_id: "" });
    setUrlReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_SHIPMENTS,
        SwayyEmployeeRoles.EDIT_SHIPMENTS,
      ])
    ) {
      return;
    }
    const nextParams = store.buildSearchParams();
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    urlReady,
    store.params.page,
    store.params.limit,
    store.params.status,
    store.params.awb,
    store.params.from_date,
    store.params.to_date,
    store.params.overdue,
  ]);

  useEffect(() => {
    if (
      !urlReady ||
      !userStore.hasSwayyPermission([
        SwayyEmployeeRoles.VIEW_SHIPMENTS,
        SwayyEmployeeRoles.EDIT_SHIPMENTS,
      ])
    ) {
      return;
    }
    void handleLoadShipments();
  }, [
    urlReady,
    store.params.page,
    store.params.limit,
    store.params.status,
    store.params.awb,
    store.params.from_date,
    store.params.to_date,
    store.params.overdue,
  ]);

  const canDownloadCsv =
    userStore.hasSwayyPermission(SwayyEmployeeRoles.EDIT_SHIPMENTS) &&
    store.statusTab === SWAYY_SHIPMENT_DEFAULT_STATUS_TAB;

  useEffect(() => {
    if (!canDownloadCsv) setCsvMenuEl(null);
  }, [canDownloadCsv]);

  const handleDownloadCsv = async (courier: SwayyCourierPartner) => {
    setCsvMenuEl(null);
    try {
      store.clearCsvError();
      await store.downloadCourierCsv(courier);
    } catch {
      showError(
        "Error",
        store.csvError || "Failed to download courier template.",
      );
    }
  };

  const handleRetry = async () => {
    try {
      await store.getShipments();
    } catch {
      showError("Error", "Failed to load shipments.");
    }
  };

  const handleApply = () => {
    store.applyFilters();
  };

  const handleClear = () => {
    store.clearFilters();
  };

  const handleClearAwb = () => {
    store.setDraftAwb("");
    store.applyFilters();
  };

  const handleAwbChange = (value: string) => {
    store.setDraftAwb(value);
    if (awbDebounceRef.current) clearTimeout(awbDebounceRef.current);
    awbDebounceRef.current = setTimeout(() => {
      store.applyFilters();
    }, 400);
  };

  const handleAction = async (
    action: SwayyShipmentAction,
    shipment: SwayyShipment,
  ) => {
    try {
      if (action === "open_order") {
        navigate(`/orders/${shipment.order_id}`);
        return;
      }
      await store.getShipmentDetail(shipment.id);
    } catch {
      showError("Error", "Failed to load shipment details.");
    }
  };

  if (
    !userStore.hasSwayyPermission([
      SwayyEmployeeRoles.VIEW_SHIPMENTS,
      SwayyEmployeeRoles.EDIT_SHIPMENTS,
    ])
  ) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        bgcolor: swayyColors.pageBg,
        minHeight: "100%",
      }}
    >
      <SwayyPageHeader
        title="Shipments"
        description="Track every package from created to delivered"
        actions={
          canDownloadCsv ? (
            <>
              <Box ref={csvButtonRef} sx={{ display: "inline-flex" }}>
                <AppButton
                  label={store.csvDownloading ? "Preparing..." : "Actions"}
                  disabled={store.csvDownloading}
                  onClick={() => {
                    try {
                      if (csvButtonRef.current) {
                        setCsvMenuEl(csvButtonRef.current);
                      }
                    } catch {
                      showError("Error", "Failed to open actions.");
                    }
                  }}
                  customStyles={{
                    minWidth: 120,
                    height: 40,
                    minHeight: 40,
                    borderRadius: swayyRadii.md,
                    bgcolor: swayyColors.pine,
                    color: swayyColors.white,
                    "&:hover": { bgcolor: swayyColors.pineHover },
                  }}
                />
              </Box>
              <Menu
                anchorEl={csvMenuEl}
                open={Boolean(csvMenuEl)}
                onClose={() => setCsvMenuEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      minWidth: 240,
                      mt: 0.75,
                      borderRadius: swayyRadii.md,
                      border: `1px solid ${swayyColors.border}`,
                    },
                  },
                }}
              >
                <MenuItem
                  disabled={store.csvDownloading}
                  onClick={() => void handleDownloadCsv("shiprocket")}
                >
                  <ListItemIcon
                    sx={{
                      color: swayyColors.textSecondary,
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  >
                    <FileDownloadOutlined />
                  </ListItemIcon>
                  <CustomTypography variant="body2">
                    Download Shiprocket CSV
                  </CustomTypography>
                </MenuItem>
                <MenuItem
                  disabled={store.csvDownloading}
                  onClick={() => void handleDownloadCsv("shipmozo")}
                >
                  <ListItemIcon
                    sx={{
                      color: swayyColors.textSecondary,
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  >
                    <FileDownloadOutlined />
                  </ListItemIcon>
                  <CustomTypography variant="body2">
                    Download Shipmozo CSV
                  </CustomTypography>
                </MenuItem>
              </Menu>
            </>
          ) : undefined
        }
      />

      <SwayySearchFiltersPanel
        open={store.filtersOpen}
        onToggle={store.toggleFiltersOpen}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SwayyFilterSearchField>
          <SwayyFilterSearchInput
            value={store.draftAwb}
            onChange={handleAwbChange}
            onClear={handleClearAwb}
            placeholder="Search AWB..."
          />
        </SwayyFilterSearchField>
        <SwayyFilterDropdownField>
          <AppSearchDropdownMulti
            title="Select Overdue"
            isMultiSelect={false}
            list={OVERDUE_OPTIONS}
            value={OVERDUE_OPTIONS.filter((option) =>
              store.draftOverdue.includes(option.id),
            )}
            {...SWAYY_FILTER_DROPDOWN_DEFAULTS}
            handleSelect={(items) =>
              store.setDraftOverdue(items.map((item) => item.id))
            }
          />
        </SwayyFilterDropdownField>
        <SwayyFilterDateField>
          <DateRangePickerNew
            placeholder="Date Range"
            startDate={
              store.draftFromDate ? dayjs(store.draftFromDate) : null
            }
            endDate={store.draftToDate ? dayjs(store.draftToDate) : null}
            onRangeSelect={(range) => {
              store.setDraftDateRange(
                range.startDate ? range.startDate.format("YYYY-MM-DD") : "",
                range.endDate ? range.endDate.format("YYYY-MM-DD") : "",
              );
            }}
          />
        </SwayyFilterDateField>
      </SwayySearchFiltersPanel>

      <SwayyStatusTabs
        value={store.statusTab}
        onChange={store.setStatusTab}
        tabs={SWAYY_SHIPMENT_STATUS_TABS}
        icons={SWAYY_SHIPMENT_STATUS_TAB_ICONS}
      />

      <SwayyShipmentsTable onAction={handleAction} onRetry={handleRetry} />
      <SwayyShipmentDetailDrawer
        onOpenOrder={(orderId) => navigate(`/orders/${orderId}`)}
      />
    </Box>
  );
};

export default observer(SwayyShipments);
